import Config from '@/models/config.model';
import FilterGroupFilterCombi from '@/classes/filter-group-filter-combi.model';
import Product from '@/models/product.model';
import Translations from '@/models/translations.model';
import FilterGroup from '@/models/filter-group.model';
import PredefinedFilterItem from '@/models/predefined-filter-item.model';
import stateMemoryManager from '@/utility/state-memory-manager';
import ProductShop from '@/models/product-shop.model';
import ProductRental from '@/models/product-rental.model';
import PromotionMaterial from '@/models/promotion-material.model';

export default class State {
    config: Config;
    translations: Translations;
    products: Array<Product>;
    productsShop: Array<ProductShop>;
    productsRental: Array<ProductRental>;
    promotionMaterials: Array<PromotionMaterial>;
    filters: Array<FilterGroup>;
    predefinedFilterItems: Array<PredefinedFilterItem>;
    selectedFilters: Array<FilterGroupFilterCombi>;
    productSorting: string;
    productLimit: number;
    locationHash: string;

    constructor(state: State | Record<string, any>) {
        this.config = state.config;
        this.translations = state.translations;
        this.products = state.products;
        this.productsShop = state.productsShop;
        this.productsRental = state.productsRental;
        this.promotionMaterials = state.promotionMaterials || [];
        this.filters = state.filters;
        this.predefinedFilterItems = state.predefinedFilterItems || [];
        this.selectedFilters = state.selectedFilters || [];
        this.productSorting = state.productSorting;
        this.productLimit = state.productLimit;
        this.locationHash = location.hash;

        this.init();
    }

    /**
     * Initializes the state
     */
    private async init(): Promise<void> {
        this.limitInitialChunkSizeForReducedMode();
        this.bindHashChange();

        const shouldUseMemoryState = this.shouldUseMemoryState();
        
        if (shouldUseMemoryState) {
            await this.initMemoryState();
        } else {
            // if memoryState shouldnt
            // be used init selected filters
            // which may be specified
            // in the location hash
            this.initSelectedFiltersFromLocationHash();
        }

        // always save the state after initializing,
        // this will ensure that old states will be overriden
        // when the user visits the PLP a second time.
        this.save();
    }

    /**
     * Limits the initial chunk size if the app
     * is in 'matno-reduced' mode
     */
    private limitInitialChunkSizeForReducedMode(): void {
        if (this.config.mode === 'matno-reduced') {
            this.config.initialChunkSize = Math.min(this.config.initialChunkSize, 6);
            this.productLimit = this.config.initialChunkSize;
        }
    }

    /**
     * Initializes the memory state
     */
    private async initMemoryState(): Promise<void> {
        const memoryState = await stateMemoryManager.getMemoryState(location.pathname);

        if (!memoryState) {
            return;
        }

        if (memoryState.selectedFilterIds?.length) {
            this.selectFilters(memoryState.selectedFilterIds);
        }

        if (memoryState.productSorting) {
            this.productSorting = memoryState.productSorting;
        }

        if (memoryState.productLimit) {
            this.productLimit = memoryState.productLimit;
        }
    }

    /**
     * Checks if the MemoryState should be used
     * to restore a former app state.
     * MemoryState should be used if the user
     * is on PLP, navigates to PDP and navigates back
     * to PLP by using the browser history
     * or if the user uses the back-link on the PDP
     * which will set the 'origin' URL parameter
     * with the value 'pdp'.
     * @returns Boolean
     */
    private shouldUseMemoryState(): boolean {
        const urlParams = new URLSearchParams(location.search);
        let shouldUseMemoryState = false;

        if (urlParams.get('origin') === 'pdp') {
            shouldUseMemoryState = true;
        } else if (window.performance && typeof window.performance.getEntriesByType === 'function') {
            // check if page is visited by navigating through browser history
            const perfEntries = window.performance.getEntriesByType('navigation');
    
            if (perfEntries.length) {
                const navigationType = (perfEntries[0] as any).type as string;
    
                if (navigationType === 'back_forward') {
                    shouldUseMemoryState = true;
                }
            }
        }
    
        return shouldUseMemoryState;
    }

    /**
     * Binds the hash change event listener
     * to update the selected filters
     * @returns 
     */
    private bindHashChange(): void {
        if (!this.filters?.length) {
            return;
        }

        window.addEventListener('hashchange', () => {
            const hash = location.hash;
            this.updateSelectedFilters(this.locationHash, hash);
            this.locationHash = hash;
            this.save();
        });
    }

    /**
     * Initializes the selected filters by reading
     * the location hash.
     * Example syntax: `products.html#1,2,3`
     */
     private async initSelectedFiltersFromLocationHash(): Promise<void> {
        if (!this.filters?.length) {
            return;
        }

        const preselectedFilterIds = this.parseLocationHash();

        if (!preselectedFilterIds.length) {
            return;
        }

        this.selectFilters(preselectedFilterIds);
    }


    /**
     * Updates the selected filters
     * by comparing the old and new 
     * location hash values after
     * it was changed
     * @param oldLocationHash - The old location hash value
     * @param newLocationHash - The new location hash value
     */
    private updateSelectedFilters(oldLocationHash: string, newLocationHash: string): void {
        const oldFilterIds = this.parseLocationHash(oldLocationHash);
        const newFilterIds = this.parseLocationHash(newLocationHash);
        const removedFilterIds = oldFilterIds.filter(filterId => filterId && newFilterIds.indexOf(filterId) === -1);
        const addedFilterIds = newFilterIds.filter(filterId => filterId && oldFilterIds.indexOf(filterId) === -1);

        // remove removed filters
        this.unselectFilters(removedFilterIds);

        // add added filters
        this.selectFilters(addedFilterIds);
    }

    /**
     * Selects the filters
     * whose IDs are contained
     * in the passed in filterIds array
     * @param filterIds - Array of filterIds to select
     */
    private selectFilters(filterIds: Array<string>): void {
        this.filters.forEach(filterGroup => {
            filterGroup.filters.forEach(filter => {
                if (filterIds.indexOf(filter.id) === -1) {
                    return;
                }

                const filterIsAlreadySelected = this.filterIsSelected(filter.id);

                // avoid adding duplicate filters
                if (filterIsAlreadySelected) {
                    return;
                }

                const filterGroupFilterCombi = new FilterGroupFilterCombi(filterGroup, filter);
                this.selectedFilters.push(filterGroupFilterCombi);
            });
        });
    }

    /**
     * Unselects the filters
     * whose IDs are contained
     * in the passed in filterIds array
     * @param filterIds - Array of filterIds to unselect
     */
    private unselectFilters(filterIds: Array<string>): void {
        this.selectedFilters = this.selectedFilters.filter(filterGroupFilterCombi => filterIds.indexOf(filterGroupFilterCombi.filter.id) === -1);
    }

    /**
     * Checks if the filter
     * with the given filterId
     * is selected
     * @param filterId - The filterId to check
     * @returns boolean
     */
     private filterIsSelected(filterId: string): boolean {
        return this.selectedFilters.some(filterGroupFilterCombi => filterGroupFilterCombi.filter.id === filterId);
    }

    /**
     * Parses the location hash and
     * returns a list of filterIds
     * @param hash Location hash value
     * @returns Array of filterIds
     */
    private parseLocationHash(hash = location.hash): Array<string> {
        hash = hash.slice(1);

        if (!hash) {
            return [];
        }

        return hash.split(',');
    }

    /**
     * Saves the state inside
     * sessionStorage
     * by using the locations 
     * pathname as the key
     * so each PLP stores
     * its own state
     */
    public save(): void {
        stateMemoryManager.save(location.pathname, this);
    }
}
